// The instanced circle batch (the plan's A.4).
//
// A circle, a ring, an arc and a dashed halo are ONE instance each, drawn as
// a signed-distance field on a square. Nothing here is ever a polyline, so
// the number of vertices does not depend on the radius and a hover ring is
// as smooth at zoom 3.5 as at 0.18.
//
// It reproduces every round mark of the map: the hover ring, the order
// rings, the control-point halo, the anchor ring and its arc, the capture
// gauge, the sonar fleet ring, the base coverage disc and ring, the pings,
// the blips and the effect rings.

import type { AttribSpec, BatchDeps } from './types';
import { StrokeBatch, STROKE_UNIFORMS } from './batch';
import { CORNER_ATTRIB, CORNER_DECODE_GLSL } from '../gl/corner';
import type { ProgramSource } from '../shaders/program';
import { circleModuleWgsl } from '../shaders/wgsl/circles';
import { TAU } from '../../render/math';

/** 13 floats, 52 bytes. */
export const CIRCLE_FLOATS = 13;

// Location 0 is the per-vertex corner of `corner.ts`, so an instance
// attribute starts at 1 (R553).
export const CIRCLE_ATTRIBS: readonly AttribSpec[] = [
  { name: 'aCenter', location: 1, size: 2, offset: 0 },
  { name: 'aRadius', location: 2, size: 1, offset: 2 },   // world px
  { name: 'aStroke', location: 3, size: 1, offset: 3 },   // <= 0 is a filled disc
  { name: 'aArc', location: 4, size: 2, offset: 4 },      // start, sweep (radians)
  { name: 'aDash', location: 5, size: 2, offset: 6 },     // count per turn, on fraction
  { name: 'aFlags', location: 6, size: 1, offset: 8 },
  { name: 'aColor', location: 7, size: 4, offset: 9 },    // straight rgba
];

/** Writes one circle instance. Pure, so a test pins the layout. */
export function writeCircle(
  out: Float32Array, at: number,
  x: number, y: number, radius: number, stroke: number,
  arcStart: number, arcSweep: number,
  dashCount: number, dashFill: number,
  flags: number, color: number, alpha: number,
): void {
  out[at] = x; out[at + 1] = y;
  out[at + 2] = radius; out[at + 3] = stroke;
  out[at + 4] = arcStart; out[at + 5] = arcSweep;
  out[at + 6] = dashCount; out[at + 7] = dashFill;
  out[at + 8] = flags;
  out[at + 9] = ((color >> 16) & 0xff) / 255;
  out[at + 10] = ((color >> 8) & 0xff) / 255;
  out[at + 11] = (color & 0xff) / 255;
  out[at + 12] = alpha;
}

export const CIRCLE_VERTEX = `#version 300 es
layout(location=0) in float aCorner;
layout(location=1) in vec2 aCenter;
layout(location=2) in float aRadius;
layout(location=3) in float aStroke;
layout(location=4) in vec2 aArc;
layout(location=5) in vec2 aDash;
layout(location=6) in float aFlags;
layout(location=7) in vec4 aColor;
uniform mat3 uViewProj;
uniform float uZoom;
uniform float uPxPerWorld;
out vec2 vP;
out float vR;
out float vHalfW;
out float vFill;
out vec2 vArc;
out vec2 vDash;
out vec4 vColor;
void main() {
${CORNER_DECODE_GLSL}
  int flags = int(aFlags + 0.5);
  float k = ((flags & 1) != 0) ? 1.0 / uZoom : 1.0;     // F_SCREEN
  float px = 1.0 / max(uPxPerWorld, 1e-6);
  bool filled = aStroke <= 0.0;
  float r = aRadius * k;
  float wWorld = filled ? 0.0 : aStroke * k;
  float wDraw = filled ? 0.0 : max(wWorld, px);
  // The quad is inflated by TWO device px and not by one. The fragment
  // ramps the edge over fwidth(d), and d moves one world px for one world
  // px of travel. A finite difference over one device px therefore moves d
  // by at most px on each axis, and fwidth adds the two axes, so it is at
  // most 2.0 * px. At an inflation of one px the distance at the four edge
  // midpoints of the quad is exactly px, which fwidth can reach, and the
  // ramp then leaves a faint tick there. MSAA made that tick visible. At
  // two px the distance on the quad edge is never less than the half-width
  // of the ramp, so the coverage is exactly zero on the edge with MSAA on
  // and with MSAA off.
  float ext = r + wDraw * 0.5 + 2.0 * px;
  vec2 local = (corner - 0.5) * (2.0 * ext);
  vec2 world = aCenter + local;
  gl_Position = vec4((uViewProj * vec3(world, 1.0)).xy, 0.0, 1.0);
  vP = local;
  vR = r;
  vHalfW = wDraw * 0.5;
  vFill = filled ? 1.0 : 0.0;
  vArc = aArc;
  vDash = aDash;
  float fade = filled ? 1.0 : min(1.0, wWorld * uPxPerWorld);
  float a = ((flags & 2) != 0) ? 0.0 : aColor.a;         // F_ADD
  vColor = vec4(aColor.rgb * aColor.a, a) * fade;
}`;

export const CIRCLE_FRAGMENT = `#version 300 es
precision highp float;
const float TAU = 6.283185307179586;
uniform float uAlpha;
in vec2 vP;
in float vR;
in float vHalfW;
in float vFill;
in vec2 vArc;
in vec2 vDash;
in vec4 vColor;
out vec4 o;
void main() {
  float d = length(vP) - vR;
  float sd = vFill > 0.5 ? d : abs(d) - vHalfW;
  // The width comes from d and not from sd: abs() puts a crease on the
  // ring centreline, and fwidth spikes exactly there.
  float w = max(fwidth(d), 1e-6);
  float cov = 1.0 - smoothstep(-w, w, sd);
  if (vArc.y < TAU || vDash.x > 0.0) {
    float rel = mod(atan(vP.y, vP.x) - vArc.x, TAU);
    if (vArc.y < TAU) cov *= step(rel, vArc.y);
    if (vDash.x > 0.0) cov *= step(fract(rel / TAU * vDash.x), vDash.y);
  }
  o = vColor * cov * uAlpha;
}`;

/** The batch's program, as both backends read it. */
export function circleProgram(): ProgramSource {
  const attribs = [CORNER_ATTRIB, ...CIRCLE_ATTRIBS];
  return {
    name: 'circles',
    glsl: { vertex: CIRCLE_VERTEX, fragment: CIRCLE_FRAGMENT },
    wgsl: circleModuleWgsl(STROKE_UNIFORMS, attribs),
    uniforms: STROKE_UNIFORMS,
    attribs,
  };
}

export class CircleBatch extends StrokeBatch {
  constructor(deps: BatchDeps, capacity = 256) {
    super(deps, 'circles', CIRCLE_FLOATS, CIRCLE_ATTRIBS, capacity);
  }

  /** A full stroked circle. */
  ring(x: number, y: number, r: number, width: number, color: number, alpha: number, flags = 0): void {
    const at = this.buf.reserve();
    writeCircle(this.buf.data, at, x, y, r, width, 0, TAU, 0, 0, flags, color, alpha);
  }

  /** A filled disc. */
  disc(x: number, y: number, r: number, color: number, alpha: number, flags = 0): void {
    const at = this.buf.reserve();
    writeCircle(this.buf.data, at, x, y, r, 0, 0, TAU, 0, 0, flags, color, alpha);
  }

  /** A stroked arc, in the PixiJS convention: a start angle and a sweep. */
  arc(
    x: number, y: number, r: number, width: number,
    start: number, sweep: number, color: number, alpha: number, flags = 0,
  ): void {
    const at = this.buf.reserve();
    writeCircle(this.buf.data, at, x, y, r, width, start, sweep, 0, 0, flags, color, alpha);
  }

  /**
   * A dashed circle. `count` is the number of dashes in a whole turn and
   * `fill` is the lit fraction of one dash, so the hover ring is
   * `(HOVER_DASHES, HOVER_DASH_FILL)` and a rotating halo turns by moving
   * `start`.
   */
  dashed(
    x: number, y: number, r: number, width: number,
    count: number, fill: number, color: number, alpha: number,
    start = 0, sweep = TAU, flags = 0,
  ): void {
    const at = this.buf.reserve();
    writeCircle(this.buf.data, at, x, y, r, width, start, sweep, count, fill, flags, color, alpha);
  }
}
